import {Button} from '@wandb/components';
import React from 'react';

import type {RunSetPart} from '../../state/views/runSet/types';
import type {Grouping} from '../../util/queryTypes';
import type {ConfigV2} from '../../util/runfeed';
import type {GroupSelectionState} from '../../util/selectionmanager';
import {DeleteRunsButton} from '../DeleteRunsButton';
import {JobDrawerLocked} from '../Launch/JobDrawer/JobDrawer';
import type {LaunchDrawerJobRun} from '../Launch/utilTypes';
import {RunMover} from '../RunMover';
import BulkTagEditor from '../Tags/BulkTagEditor';
import {MoveRunDrawerBulkButton} from './MoveRunDrawer/MoveRunDrawerBulk';
import {useRunSelectorFeatureEnabled} from './RunSelectorFeaturesContext';
import {useRunSelectorProjectInfo} from './RunSelectorProjectContext';
import {RunsFreezeAndFilter} from './RunsFreeze';
import {RunsGroupTableAction} from './RunsGrouping';
import {RunsSortTableAction} from './RunsSort';

type RunSelectorExtraTableActionsLeftProps = {
  closeLaunchConfigModal: () => void;
  enableFreezeRunset: boolean;
  enableSetProject: boolean;
  groupTempSelection: GroupSelectionState;
  hasEntityStorageEnabled: boolean;
  isExpandable: boolean;
  isExpanded: boolean;
  isLaunchActive: boolean;
  launchDrawerJobRun: LaunchDrawerJobRun | null;
  onAddTag: (tag: string) => void;
  onCloseSortPopup: () => void;
  onCreateSweepWithPriorRuns: () => void;
  onDeleteArtifacts: (deleteArtifacts: boolean) => void;
  onMoveRuns: (entity: string, projectId: string) => Promise<string>;
  onMovedRuns: () => void;
  onOpenSortPopup: () => void;
  onReload: () => void;
  onRemoveTag: (tag: string) => void;
  recommendedGrouping: Grouping | undefined;
  resetPagination: () => void;
  runSet: RunSetPart;
  setProject: (
    newEntityName: string,
    newProjectName: string,
    newProjectId: string
  ) => void;
  sortPopupOpen: boolean;
  tableSettings: ConfigV2;
  tags: {name: string; colorIndex: number | undefined; count: number}[];
  tempOutputArtifactsCount: number;
  tempSelectedActiveCount: number;
  tempSelectedCount: number;
};

export function RunSelectorExtraTableActionsLeft(
  props: RunSelectorExtraTableActionsLeftProps
) {
  const isEnabled = useRunSelectorFeatureEnabled();
  const {projectName, entityName, projectInternalId} =
    useRunSelectorProjectInfo();

  return (
    <>
      {props.isLaunchActive && (
        <JobDrawerLocked
          open={props.launchDrawerJobRun != null}
          closeDrawer={props.closeLaunchConfigModal}
          entityName={entityName}
          projectName={projectName}
          jobId={props.launchDrawerJobRun?.jobId ?? ''}
          jobVersion={props.launchDrawerJobRun?.jobVersion ?? ''}
          defaultRunToClone={props.launchDrawerJobRun?.runName}
        />
      )}
      {isEnabled('filter') && (
        <RunsFreezeAndFilter
          enableFreezeRunset={props.enableFreezeRunset}
          filtersRef={props.runSet.filtersRef}
          entityName={entityName}
          projectName={projectName}
          compact={props.isExpandable && !props.isExpanded}
          onChange={() => {
            props.resetPagination();
          }}
          tableSettings={props.tableSettings}
        />
      )}
      {isEnabled('group') && (
        <RunsGroupTableAction
          entityName={entityName}
          projectName={projectName}
          groupSelectionsRef={props.runSet.groupSelectionsRef}
          recommendedGrouping={props.recommendedGrouping}
          compact={props.isExpandable && !props.isExpanded}
          onGroupingChanged={() => {
            props.resetPagination();
          }}
          tableSettings={props.tableSettings}
        />
      )}
      {isEnabled('sort') && (
        <RunsSortTableAction
          entityName={entityName}
          projectName={projectName}
          sortRef={props.runSet.sortRef}
          compact={props.isExpandable && !props.isExpanded}
          open={props.sortPopupOpen}
          onOpen={() => props.onOpenSortPopup()}
          onClose={() => props.onCloseSortPopup()}
          onSortChanged={() => props.resetPagination()}
          tableSettings={props.tableSettings}
        />
      )}
      {props.isExpandable && !props.isExpanded ? (
        <></>
      ) : (
        <>
          {/* BATCH TAGGING, MOVING, DELETING */}
          {isEnabled('tag') && (
            <BulkTagEditor
              // TODO(views): Here and below, this is a bad way to pass these counts through as it
              // won't always update.
              selectedCount={props.tempSelectedCount}
              tags={props.tags}
              addTag={props.onAddTag}
              removeTag={props.onRemoveTag}
            />
          )}
          {isEnabled('move') && props.tempSelectedCount ? (
            <MoveRunDrawerBulkButton
              entityName={entityName}
              groupTempSelection={props.groupTempSelection}
              onMovedRuns={props.onMovedRuns}
              projectName={projectName}
              groupSelectionsRef={props.runSet.groupSelectionsRef}
              selectedCount={props.tempSelectedCount}
            />
          ) : null}

          {isEnabled('move') && projectInternalId && (
            <RunMover
              entityName={entityName}
              // prevent users from trying to move runs to another entity if the entity is using external storage
              // because this operation does not move the run files
              requireSameEntity={props.hasEntityStorageEnabled}
              requireSameEntityReason={
                "Can't move runs between entities when using external storage"
              }
              projectInternalId={projectInternalId}
              projectName={projectName}
              selectedCount={props.tempSelectedCount}
              refetch={props.onReload}
              onMove={props.onMoveRuns}
            />
          )}
          {isEnabled('delete') && (
            <DeleteRunsButton
              selectedCount={props.tempSelectedCount}
              onDelete={props.onDeleteArtifacts}
              outputArtifactsCount={props.tempOutputArtifactsCount}
              activeRunsCount={props.tempSelectedActiveCount}
            />
          )}
          {isEnabled('create-sweep') && (
            <Button
              startIcon="sweeps-broom"
              variant="secondary"
              onPress={props.onCreateSweepWithPriorRuns}>
              New sweep
            </Button>
          )}
        </>
      )}
    </>
  );
}
